import React, { useState, useEffect } from 'react';
import { Box, Alert } from '@mui/material';
import MappingDiagram from './MappingDiagram';
import MappingControls from './MappingControls';
import useMappingAnswer from './useMappingAnswer';
import { validateCompleteness, defaultMappingConfig } from './mappingUtils';

/**
 * Student-facing component for creating mapping connections
 * Allows students to connect items from left column to right column
 */
const MappingStudent = ({
  config = defaultMappingConfig,
  value = '',
  onChange,
  disabled = false,
  showValidation = false,
}) => {
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [validationMessage, setValidationMessage] = useState(null);

  const {
    connections,
    selectedLeft,
    canUndo,
    canClear,
    handleLeftClick,
    handleRightClick,
    handleConnectionClick,
    undo,
    clear,
    syncWithExternalValue,
  } = useMappingAnswer(value, config, onChange);

  // Sync with external value changes
  useEffect(() => {
    syncWithExternalValue(value);
  }, [value, syncWithExternalValue]);

  // Validate completeness when showing validation
  useEffect(() => {
    if (showValidation) {
      const validation = validateCompleteness(connections, config);
      if (!validation.complete) {
        setValidationMessage(validation.reason);
      } else {
        setValidationMessage(null);
      }
    }
  }, [connections, config, showValidation]);

  const handleUndo = () => {
    undo();
  };

  const handleClear = () => {
    clear();
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Controls */}
      {!disabled && (
        <MappingControls
          onUndo={handleUndo}
          onClear={handleClear}
          canUndo={canUndo}
          canClear={canClear}
          disabled={disabled}
        />
      )}

      {/* Mapping diagram */}
      <MappingDiagram
        leftItems={config.leftItems || []}
        rightItems={config.rightItems || []}
        connections={connections}
        selectedLeft={selectedLeft}
        hoveredConnection={hoveredConnection}
        onLeftClick={disabled ? undefined : handleLeftClick}
        onRightClick={disabled ? undefined : handleRightClick}
        onConnectionClick={disabled ? undefined : handleConnectionClick}
        onConnectionHover={setHoveredConnection}
        readOnly={disabled}
        config={config}
      />

      {/* Validation message */}
      {showValidation && validationMessage && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {validationMessage}
        </Alert>
      )}

      {/* Instructions */}
      {!disabled && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: 'action.hover',
            borderRadius: 1,
            fontSize: '0.85rem',
            color: 'text.secondary',
          }}
        >
          <strong>Instructions:</strong>
          <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
            <li>Click an item on the left to select it</li>
            <li>Click an item on the right to create a connection</li>
            <li>Click on an arrow to remove a connection</li>
          </ul>
        </Box>
      )}
    </Box>
  );
};

export default MappingStudent;
